/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { DataTableWrapper } from '../datatable/data-table'
import { columns, PageLockForTable, columnDisplayNames } from './lock_table/columns'
import { PageLock } from '../PageLockProvider';
import { AppContext } from '@/App';
import { getPageName } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

export type LockColumnVisibility = {
    id: boolean,
    userName: boolean,
    projectName: boolean,
    pageName: boolean,
    lockTime: boolean,
    isLocked: boolean,
}

const LockManager = () => {
    const appContext = React.useContext(AppContext);
    const isMobile = useIsMobile();
    const [loading, setLoading] = useState<boolean>(true); // add a loading state
    const [locks, setLocks] = useState<PageLockForTable[]>([]); // assuming you have a PageLock type

    React.useEffect(() => {
        document.body.style.cursor = 'wait';
        fetch("Lock/")
            .then(response => response.json())
            .then(data => {
                const formattedData: PageLockForTable[] = data.map((lock: PageLock) => ({
                    ...lock,
                    projectName: appContext.projects.find((p) => p.id === lock.projectId)!.name, // Replace with actual project name logic
                    pageName: getPageName(lock.page), // Replace with actual page name logic
                }));
                setLocks(formattedData);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching locks:', error);
                setLoading(false);
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }, []);

    function unlock(id: number) {
        document.body.style.cursor = 'wait';
        fetch(`Lock/Unlock/${id}`, {
            method: 'PUT',
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to unlock the page');
                }
                // update locks in setLocks
                setLocks(prevLocks => 
                    prevLocks.map(lock => 
                        lock.id === id ? { ...lock, isLocked: false } : lock
                    )
                );
                
            })
            .catch(error => {
                console.error('Error unlocking page:', error);
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    const groupActions = [
        {
            name: "Unlock",
            action: (selectedRows: any[]) => {
                // Handle unlock action for selected rows
                selectedRows.forEach(row => {
                    unlock(row.id);
                });
            },
        }
    ];

    function loadColumnVisibility(): LockColumnVisibility {
        const localStorageValue = localStorage.getItem(appContext.applicationUser?.id + "_Locks_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                id: visibilityValues.find((v) => v.columnId === "id")?.isVisible ?? false,
                userName: visibilityValues.find((v) => v.columnId === "userName")?.isVisible ?? true,
                projectName: visibilityValues.find((v) => v.columnId === "projectName")?.isVisible ?? true,
                pageName: visibilityValues.find((v) => v.columnId === "pageName")?.isVisible ?? true,
                lockTime: visibilityValues.find((v) => v.columnId === "lockTime")?.isVisible ?? true,
                isLocked: visibilityValues.find((v) => v.columnId === "isLocked")?.isVisible ?? true,
            })
        }
        return {
            id: false,
            userName: true,
            projectName: true,
            pageName: true,
            lockTime: true,
            isLocked: true,
        }
    }

    return (
        <div className={`${isMobile ? "" : "m-2 p-2"}`}>
            {!isMobile && 
            <div className="flex flex-row justify-between">
                <h1 className="text-2xl my-auto p-2">Locks</h1>
            </div>}
            <DataTableWrapper
                columns={columns(
                    (id: number) => unlock(id),
                )}
                table_title="Users"
                columnDisplayNames={columnDisplayNames}
                data={locks}
                setData={setLocks}
                loading={loading}
                viewOnly={appContext.authLevel < 4}
                groupActions={groupActions}
                columnVisibility={loadColumnVisibility()}
            />
        </div>
    )
}

export default LockManager